import {
  MAX_DIALOG_CLOSE_CHAIN_DEPTH,
  isPlatformDialogSlot,
  resolveClosingSlotId,
  resolveDialogCloseConfigForSlot,
  type DialogCloseListener,
} from '@/services/action/dialogCloseSlotContext'
import {
  resolveCloseRootsListenersFromProperties,
  resolveListenersFromProperties,
  shouldTriggerCloseRootsAfterNewThread,
  shouldTriggerStage3DialogClose,
} from '@/services/action/stage3DialogClosePolicy'
import {
  INITIAL_JS_RESULT_PROPERTIES,
  type ActionContext,
  type ActionDependencies,
  type JsResultProperties,
} from '@/services/action/types'
import { extractActionTextValue } from '@/services/action/normalizeActionTransfer'
import { resolveDialogCloseParentContext } from '@/common/utils/resolveDialogCloseParentStoreKey'

export type DialogCloseSlotResult = {
  closed: boolean
  postScriptProperties: JsResultProperties
}

export type CloseDialogSlotFn = (
  closingSlotId: string,
  context: ActionContext,
  dependencies: ActionDependencies | undefined,
  listeners: DialogCloseListener[],
  dialogPageSnapshot: Record<string, unknown> | undefined,
  mergedCloseFields?: Record<string, unknown>,
) => Promise<DialogCloseSlotResult>

export type InvokeDialogCloseFromPropertiesOptions = {
  context: ActionContext
  props: JsResultProperties
  dependencies?: ActionDependencies
  closeDialogSlot: CloseDialogSlotFn
  resolvePagesDataSlotId: (
    ctx: ActionContext,
    deps?: ActionDependencies,
  ) => string | undefined
  depth?: number
  /** Post-chain UniData of the closing action (Swing incomingData). */
  mergedCloseFields?: Record<string, unknown>
}

function readActionIsContextXml(context: ActionContext): string {
  return String(extractActionTextValue(context.data?.isContext) ?? '').trim()
}

export async function invokeDialogCloseFromProperties(
  options: InvokeDialogCloseFromPropertiesOptions,
): Promise<{ closed: boolean; abortAction: boolean }> {
  const { context, dependencies, closeDialogSlot, resolvePagesDataSlotId } = options
  const appStore = dependencies?.appStore
  if (!appStore) return { closed: false, abortAction: false }

  const depth = options.depth ?? 0
  if (depth >= MAX_DIALOG_CLOSE_CHAIN_DEPTH) {
    return { closed: false, abortAction: false }
  }

  let props = options.props
  const isContextXml = readActionIsContextXml(context)

  if (!shouldTriggerStage3DialogClose(props, isContextXml)) {
    return { closed: false, abortAction: false }
  }

  let closingSlotId = resolveClosingSlotId(
    context,
    appStore,
    resolvePagesDataSlotId,
    dependencies,
  )
  if (!closingSlotId) {
    return { closed: false, abortAction: false }
  }


  let closedAny = false
  let chainDepth = depth

  while (
    chainDepth < MAX_DIALOG_CLOSE_CHAIN_DEPTH &&
    closingSlotId &&
    shouldTriggerStage3DialogClose(props, isContextXml)
  ) {
    const listeners = resolveListenersFromProperties(props, isContextXml)
    if (listeners.length === 0) {
      break
    }


    const dialogPageSnapshot = appStore.pagesData?.[closingSlotId] as
      | Record<string, unknown>
      | undefined

    const slotResult = await closeDialogSlot(
      closingSlotId,
      context,
      dependencies,
      listeners,
      dialogPageSnapshot,
      options.mergedCloseFields,
    )

    if (!slotResult.closed) break

    closedAny = true
    ;(context as unknown as Record<string, unknown>).__closedViaProperties = true

    props = slotResult.postScriptProperties

    const dialogConfig = resolveDialogCloseConfigForSlot(appStore, closingSlotId)
    const parentContext = resolveDialogCloseParentContext(appStore, dialogConfig, {
      closingDialogId: closingSlotId,
    })
    const parentSlot = parentContext.storeKey
    if (!parentSlot || parentSlot === closingSlotId) break

    const shouldContinue = shouldTriggerStage3DialogClose(props, isContextXml)

    if (!shouldContinue) break

    closingSlotId = parentSlot
    chainDepth++
  }


  return { closed: closedAny, abortAction: closedAny }
}

export type InvokeCloseRootsFromPropertiesOptions = {
  context: ActionContext
  props: JsResultProperties
  windowType?: string | null
  dependencies?: ActionDependencies
  closeDialogSlot: CloseDialogSlotFn
  resolvePagesDataSlotId: (
    ctx: ActionContext,
    deps?: ActionDependencies,
  ) => string | undefined
  /** Slot where the action ran (Swing getRoot), captured before NEW_WINDOW navigation. */
  sourceSlotId?: string | null
  /** Slot created by this NEW_WINDOW / NEW_DIALOG — never close it. */
  openedSlotId?: string | null
  /** Post-chain UniData of the closing action (Swing incomingData). */
  mergedCloseFields?: Record<string, unknown>
}

function normalizeSlotId(slotId: string | null | undefined): string {
  return String(slotId ?? '').trim()
}

/**
 * Swing getRoot(action) for closeRoots: only a dialog, never a document tab.
 * `$parentPageId` on 2805 / NEW_WINDOW tabs is not a dialog — closing it
 * removePageAndDescendants would delete the just-opened banner.
 */
export function isCloseRootsDialogSlot(
  appStore: {
    dialogIds?: string[]
    pagesData?: Record<string, unknown>
  },
  slotId: string,
): boolean {
  return isPlatformDialogSlot(appStore, slotId)
}

/**
 * Swing getRoot(action): close the dialog that owned the action, never the new thread.
 */
export function resolveCloseRootsSlotId(
  appStore: {
    topDialog?: { id?: string | number } | null
    dialogIds?: string[]
    pagesData?: Record<string, unknown>
  },
  options: {
    sourceSlotId?: string | null
    openedSlotId?: string | null
    actionSlot?: string | null
    formId?: string | null
  },
): string | undefined {
  const opened = normalizeSlotId(options.openedSlotId)
  const tryDialog = (slotId: string | null | undefined): string | undefined => {
    const id = normalizeSlotId(slotId)
    if (!id || (opened && id === opened)) return undefined
    return isCloseRootsDialogSlot(appStore, id) ? id : undefined
  }

  const source = normalizeSlotId(options.sourceSlotId)
  if (source) {
    return tryDialog(source)
  }

  return tryDialog(options.actionSlot) ?? tryDialog(options.formId)
}

/**
 * Swing ActionCall.closeRoots after NEW_WINDOW / NEW_DIALOG.
 * Closes the action form dialog; does not abort stage4 (call after transfer).
 */
export async function invokeCloseRootsFromProperties(
  options: InvokeCloseRootsFromPropertiesOptions,
): Promise<{ closed: boolean }> {
  const { context, dependencies, closeDialogSlot, resolvePagesDataSlotId, windowType } = options
  const appStore = dependencies?.appStore
  if (!appStore) return { closed: false }

  const props = options.props
  if (!shouldTriggerCloseRootsAfterNewThread(props, windowType)) {
    return { closed: false }
  }

  const listeners = resolveCloseRootsListenersFromProperties(props)
  if (listeners.length === 0) return { closed: false }

  const source = normalizeSlotId(options.sourceSlotId)
  const actionSlot = source
    ? source
    : resolvePagesDataSlotId(context, dependencies)
  const closingSlotId = resolveCloseRootsSlotId(appStore, {
    sourceSlotId: options.sourceSlotId,
    openedSlotId: options.openedSlotId,
    actionSlot,
    formId: context.formId != null ? String(context.formId) : '',
  })
  if (!closingSlotId) return { closed: false }

  const dialogPageSnapshot = appStore.pagesData?.[closingSlotId] as
    | Record<string, unknown>
    | undefined

  const slotResult = await closeDialogSlot(
    closingSlotId,
    context,
    dependencies,
    listeners,
    dialogPageSnapshot,
    options.mergedCloseFields,
  )

  if (slotResult.closed) {
    ;(context as unknown as Record<string, unknown>).__closedViaProperties = true
  }

  return { closed: slotResult.closed }
}

export function emptyDialogCloseProperties(): JsResultProperties {
  return { ...INITIAL_JS_RESULT_PROPERTIES }
}

const OPENED_PAGE_SLOT_KEY = '__openedPageSlotId'

/** Remember NEW_WINDOW / NEW_DIALOG slot so closeRoots never closes the new thread. */
export function stampCloseRootsOpenedSlotId(
  context: ActionContext,
  slotId: string | null | undefined,
): void {
  const id = String(slotId ?? '').trim()
  if (!id) return
  ;(context as unknown as Record<string, unknown>)[OPENED_PAGE_SLOT_KEY] = id
}

export function readCloseRootsOpenedSlotId(context: ActionContext): string {
  return String(
    (context as unknown as Record<string, unknown>)[OPENED_PAGE_SLOT_KEY] ?? '',
  ).trim()
}
